import {
  OmsFilterOperator,
  OmsRecordFilter,
  OmsReport,
} from "../../lusca-search-api/generated-src";

export type OmsFilterCondition = OmsRecordFilter & {
  id: number;
  valueNotice?: string;
};

// Keep aligned with the producer's VALUE_CHOICE_COLUMNS. Older snapshots may
// contain suggestions for identifiers or free text; they do not opt those in.
export const OMS_VALUE_CHOICE_COLUMNS = [
  "channel",
  "company",
  "gl_product_line",
  "openfunc_curr",
  "marketplace_id",
  "account",
  "bucket",
  "region",
  "state",
  "cost_center",
  "location",
  "country",
  "declared_country_code",
  "matcher_type",
  "settype",
  "keytype",
  "last_dowen_event_type",
  "last_dowen_sub_condition_detail",
  "last_suben_status",
  "suben_cancellation_reason",
  "last_suben_status_transition_reason",
] as const;

export function supportsOmsChoices(column: string) {
  return OMS_VALUE_CHOICE_COLUMNS.some((candidate) => candidate === column);
}

export function prefersOmsChoices(report: OmsReport, column: string) {
  const choices = report.columnValues?.[column];
  return (
    supportsOmsChoices(column) &&
    (report.columnTypes?.[column] || "string") === "string" &&
    choices?.complete &&
    choices.values.length > 0
  );
}

export function conditionOperators(
  report: OmsReport,
  criterion: OmsFilterCondition
): OmsFilterOperator[] {
  const type = report.columnTypes?.[criterion.column] || "string";
  const allowed: OmsFilterOperator[] =
    type === "string"
      ? ["eq", "ne", "contains", "notContains", "isNull", "isNotNull"]
      : ["eq", "ne", "gt", "gte", "lt", "lte", "isNull", "isNotNull"];
  if (
    type === "string" &&
    (prefersOmsChoices(report, criterion.column) ||
      criterion.values !== undefined ||
      ["in", "notIn"].includes(criterion.operator))
  )
    allowed.unshift("in", "notIn");
  return allowed;
}

export function changeCondition(
  criterion: OmsFilterCondition,
  operator: OmsFilterOperator
): OmsFilterCondition {
  if (criterion.operator === operator) return criterion;
  const next: OmsFilterCondition = {
    ...criterion,
    operator,
    valueNotice: undefined,
  };
  if (["isNull", "isNotNull"].includes(operator)) return next;
  if (["in", "notIn"].includes(operator)) {
    next.values =
      criterion.values ??
      (criterion.value !== undefined ? [criterion.value] : []);
    return next;
  }
  if (
    ["in", "notIn"].includes(criterion.operator) ||
    (criterion.value === undefined && criterion.values !== undefined)
  ) {
    next.value =
      criterion.values?.length === 1 ? criterion.values[0] : undefined;
    if (criterion.values && criterion.values.length > 1)
      next.valueNotice =
        "This condition needs one value. Your selected values are kept when you switch back.";
  }
  return next;
}

export function submittedCondition(
  criterion: OmsFilterCondition
): OmsRecordFilter {
  const { column, operator, value, values } = criterion;
  if (["isNull", "isNotNull"].includes(operator)) return { column, operator };
  if (["in", "notIn"].includes(operator))
    return { column, operator, values: values || [] };
  return { column, operator, value: value ?? "" };
}
